import { nanoid } from "nanoid";
import type { Cable, System, SystemNode } from "./types";
import { deepClone } from "./utils";

export type ClipboardContent = {
  nodes: SystemNode[];
  cables: Cable[];
  pasteCount: number;
};

let clipboard: ClipboardContent | null = null;

export function getClipboard(): ClipboardContent | null {
  return clipboard;
}

export function clearClipboard(): void {
  clipboard = null;
}

/** A cable is copied only when both endpoints are, or the paste lands a dangling ref. */
export function snapshotSelectionToClipboard(
  selectionNodeIds: string[],
  system: System,
): ClipboardContent | null {
  if (selectionNodeIds.length === 0) {
    clipboard = null;
    return null;
  }
  const selected = new Set(selectionNodeIds);
  const nodes = system.nodes
    .filter((n) => selected.has(n.id))
    .map((n) => deepClone(n));
  const cables = system.cables
    .filter((c) => selected.has(c.fromNodeId) && selected.has(c.toNodeId))
    .map((c) => deepClone(c));
  clipboard = { nodes, cables, pasteCount: 0 };
  return clipboard;
}

export type ClonePlan = {
  nodes: SystemNode[];
  cables: Cable[];
};

/**
 * `livingNodeIds` drops a parent ref to a zone deleted since the copy; omit it to
 * keep every ref.
 */
export function cloneNodesAndCables(
  nodes: SystemNode[],
  cables: Cable[],
  offset: number,
  livingNodeIds?: Set<string>,
): ClonePlan {
  const idMap = new Map<string, string>();
  const newNodes: SystemNode[] = [];
  for (const n of nodes) {
    const newId = nanoid(8);
    idMap.set(n.id, newId);
    const cloned = deepClone(n);
    cloned.id = newId;
    cloned.position = { x: n.position.x + offset, y: n.position.y + offset };
    newNodes.push(cloned);
  }
  for (const n of newNodes) {
    if (!n.parentId) continue;
    if (idMap.has(n.parentId)) {
      n.parentId = idMap.get(n.parentId)!;
    } else if (livingNodeIds && !livingNodeIds.has(n.parentId)) {
      delete n.parentId;
    }
  }
  const newCables: Cable[] = [];
  for (const c of cables) {
    if (!idMap.has(c.fromNodeId) || !idMap.has(c.toNodeId)) continue;
    const cloned = deepClone(c);
    cloned.id = nanoid(8);
    cloned.fromNodeId = idMap.get(c.fromNodeId)!;
    cloned.toNodeId = idMap.get(c.toNodeId)!;
    newCables.push(cloned);
  }
  return { nodes: newNodes, cables: newCables };
}

/** Null when nothing has been copied. Not pure — bumps `pasteCount`. */
export function planClipboardPaste(livingNodeIds: Set<string>): ClonePlan | null {
  if (!clipboard || clipboard.nodes.length === 0) return null;
  const offset = 24 * (clipboard.pasteCount + 1);
  const plan = cloneNodesAndCables(clipboard.nodes, clipboard.cables, offset, livingNodeIds);
  clipboard.pasteCount += 1;
  return plan;
}
